import { auth } from '../lib/firebase';
import { getApiUrl } from '../config/api';
import { findLocalBrokerLogo, FALLBACK_BROKERS, type SnaptradeBroker } from '../lib/snaptradeBrokers';

/**
 * Accès au backend SnapTrade (`/api/snaptrade/*`).
 *
 * Le `userSecret` SnapTrade ne quitte jamais le serveur : le front ne manipule
 * que des URL de portail et des identifiants de connexion.
 */

/**
 * La section "Courtiers & banques" (catalogue SnapTrade grand public, dans
 * AccountManager) est prête côté code mais pas encore activée publiquement —
 * tant que ce flag est à `false`, l'UI affiche à la place un état « prochainement
 * disponible » et masque la grille de courtiers, sans rien changer au reste
 * (recherche, plateformes de trading direct, logique de connexion SnapTrade
 * ci-dessous) qui reste intact et inchangé.
 *
 * Le jour de l'activation : passer ce flag à `true`, rien d'autre à modifier.
 */
export const SNAPTRADE_BROKERS_PUBLIC_LAUNCH = false;

export type SnaptradeConnection = {
  id: string;
  brokerName: string | null;
  brokerSlug: string | null;
  disabled: boolean;
  createdDate: string | null;
  updatedDate: string | null;
};

export type SnaptradeStatus = {
  registered: boolean;
  connections: SnaptradeConnection[];
  accounts: Array<{
    id: string;
    name: string;
    currency: string;
    currentBalance: number;
    brokerName: string | null;
    brokerSlug: string | null;
    connectionId: string | null;
  }>;
};

export type SnaptradeSyncResult = {
  accountId: string;
  snaptradeAccountId?: string;
  activities?: number;
  matchedTrades?: number;
  newTrades?: number;
  openPositions?: number;
  currentBalance?: number;
  error?: string;
};

export type SnaptradeImportResult = {
  accounts: Array<{ accountId: string; created: boolean; snaptradeAccountId: string }>;
  syncs: SnaptradeSyncResult[];
};

/** Erreur portant le code métier renvoyé par le backend (ex. `snaptrade_not_configured`). */
export class SnaptradeError extends Error {
  code?: string;
  status: number;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'SnaptradeError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new SnaptradeError('Vous devez être connecté.', 401);
  }

  const response = await fetch(getApiUrl(`/api/snaptrade${path}`), {
    ...init,
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  const raw = await response.text();
  let payload: any = null;
  try {
    payload = raw ? JSON.parse(raw) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new SnaptradeError(
      payload?.error || `Erreur SnapTrade (${response.status})`,
      response.status,
      payload?.code
    );
  }

  return payload as T;
}

export const snaptradeService = {
  /**
   * Liste des courtiers disponibles. En cas d'échec on retombe sur le catalogue
   * local : l'utilisateur peut toujours ouvrir le portail sans présélection.
   */
  async getBrokerages(): Promise<{ brokers: SnaptradeBroker[]; fromApi: boolean }> {
    try {
      const data = await request<{ brokerages: Array<Omit<SnaptradeBroker, 'logo'> & { logoUrl: string | null }> }>(
        '/brokerages'
      );

      const brokers: SnaptradeBroker[] = (data?.brokerages || []).map((brokerage) => ({
        id: brokerage.id ?? null,
        slug: brokerage.slug ?? null,
        name: brokerage.name,
        logo: findLocalBrokerLogo(brokerage.slug, brokerage.name) || brokerage.logoUrl || null,
        maintenanceMode: Boolean(brokerage.maintenanceMode),
        isDegraded: Boolean(brokerage.isDegraded),
      }));

      if (brokers.length === 0) return { brokers: FALLBACK_BROKERS, fromApi: false };
      return { brokers, fromApi: true };
    } catch {
      return { brokers: FALLBACK_BROKERS, fromApi: false };
    }
  },

  /** URL du Connection Portal (à charger dans une iframe ou un onglet). */
  async createConnectionLink(options: {
    broker?: string | null;
    reconnect?: string | null;
    locale?: string;
    darkMode?: boolean;
    immediateRedirect?: boolean;
  } = {}): Promise<{ redirectURI: string; sessionId: string | null }> {
    return request('/connect', {
      method: 'POST',
      body: JSON.stringify({
        broker: options.broker || undefined,
        reconnect: options.reconnect || undefined,
        locale: options.locale,
        darkMode: options.darkMode,
        immediateRedirect: options.immediateRedirect === true,
      }),
    });
  },

  /** Import des comptes d'une connexion fraîchement établie + première synchro. */
  async importConnection(authorizationId: string): Promise<SnaptradeImportResult> {
    return request(`/connections/${encodeURIComponent(authorizationId)}/import`, { method: 'POST' });
  },

  async syncAccount(accountId: string): Promise<SnaptradeSyncResult> {
    return request(`/accounts/${encodeURIComponent(accountId)}/sync`, { method: 'POST' });
  },

  async syncAll(): Promise<{ results: SnaptradeSyncResult[] }> {
    return request('/sync-all', { method: 'POST' });
  },

  async getStatus(): Promise<SnaptradeStatus> {
    return request('/status');
  },

  async deleteConnection(connectionId: string): Promise<{ success: boolean; affectedAccounts: string[] }> {
    return request(`/connections/${encodeURIComponent(connectionId)}`, { method: 'DELETE' });
  },
};
